import formatNumber from '@/utils/NumberFormatter';

interface IPreviousPeriodStats {
  revenue: number;
  impressions: number;
  averageCPM: number;
  efficiency: number;
  bidRate: number;
  winRate: number;
  responseRate: number;
  averageResponseTime: number;
}

interface IYieldManagerBidStatApi {
  win_rate: number;
  bid_rate: number;
  response_rate: number;
  average_cpm: number;
  average_response_time: number;
  efficiency: number;
  impressions: number;
  revenue: number;
}

export default class YieldManagerBidStat {
  id: number | string;
  name: string;
  winRate: number;
  bidRate: number;
  responseRate: number;
  averageCPM: number;
  averageResponseTime: number;
  efficiency: number;
  impressions: number;
  revenue: number;
  previousPeriodStats: IPreviousPeriodStats | null;

  constructor({
    id,
    winRate,
    bidRate,
    responseRate,
    averageCPM,
    averageResponseTime,
    efficiency,
    impressions,
    revenue,
    previousPeriodStats,
  }: {
    id: number | string;
    winRate: number;
    bidRate: number;
    responseRate: number;
    averageCPM: number;
    averageResponseTime: number;
    efficiency: number;
    impressions: number;
    revenue: number;
    previousPeriodStats: IPreviousPeriodStats | null;
  }) {
    this.id = id;
    this.name = `${id}`;
    this.winRate = winRate;
    this.bidRate = bidRate;
    this.responseRate = responseRate;
    this.averageCPM = averageCPM;
    this.averageResponseTime = averageResponseTime;
    this.efficiency = efficiency;
    this.impressions = impressions;
    this.revenue = revenue;
    this.previousPeriodStats = previousPeriodStats;
  }

  get revenueFormatted() {
    return formatNumber(Math.round(this.revenue), '$0.0a');
  }

  get impressionsFormatted() {
    return formatNumber(Math.round(this.impressions), '0.0a');
  }

  get averageCPMFormatted() {
    return formatNumber(Math.round(this.averageCPM * 100) / 100, '$0.00');
  }

  get efficiencyFormatted() {
    return formatNumber(this.efficiency, '0.0a%');
  }

  get bidRateFormatted() {
    return formatNumber(this.bidRate, '0.0a%');
  }

  get winRateFormatted() {
    return formatNumber(this.winRate, '0.0a%');
  }

  get responseRateFormatted() {
    return formatNumber(this.responseRate, '0.0a%');
  }

  get averageResponseTimeFormatted() {
    return `${this.averageResponseTime}ms`;
  }

  get revenueTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.revenue === 0) return null;
    return (this.revenue - this.previousPeriodStats.revenue) / this.previousPeriodStats.revenue;
  }

  get impressionsTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.impressions === 0) return null;
    return (this.impressions - this.previousPeriodStats.impressions) / this.previousPeriodStats.impressions;
  }

  get averageCPMTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.averageCPM === 0) return null;
    return (this.averageCPM - this.previousPeriodStats.averageCPM) / this.previousPeriodStats.averageCPM;
  }

  get efficiencyTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.efficiency === 0) return null;
    return (this.efficiency - this.previousPeriodStats.efficiency) / this.previousPeriodStats.efficiency;
  }

  get bidRateTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.bidRate === 0) return null;
    return (this.bidRate - this.previousPeriodStats.bidRate) / this.previousPeriodStats.bidRate;
  }

  get winRateTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.winRate === 0) return null;
    return (this.winRate - this.previousPeriodStats.winRate) / this.previousPeriodStats.winRate;
  }

  get responseRateTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.responseRate === 0) return null;
    return (this.responseRate - this.previousPeriodStats.responseRate) / this.previousPeriodStats.responseRate;
  }

  get averageResponseTimeTrend() {
    if (this.previousPeriodStats == null || this.previousPeriodStats.averageResponseTime === 0) return null;
    return (
      (this.averageResponseTime - this.previousPeriodStats.averageResponseTime) /
      this.previousPeriodStats.averageResponseTime
    );
  }

  get revenueTrendFormatted() {
    return formatNumber(this.revenueTrend, '+0.0a%');
  }

  get impressionsTrendFormatted() {
    return formatNumber(this.impressionsTrend, '+0.0a%');
  }

  get averageCPMTrendFormatted() {
    return formatNumber(this.averageCPMTrend, '+0.0a%');
  }

  get efficiencyTrendFormatted() {
    return formatNumber(this.efficiencyTrend, '+0.0a%');
  }

  get bidRateTrendFormatted() {
    return formatNumber(this.bidRateTrend, '+0.0a%');
  }

  get winRateTrendFormatted() {
    return formatNumber(this.winRateTrend, '+0.0a%');
  }

  get responseRateTrendFormatted() {
    return formatNumber(this.responseRateTrend, '+0.0a%');
  }

  get averageResponseTimeTrendFormatted() {
    return formatNumber(this.averageResponseTimeTrend, '+0.0a%');
  }

  static fromApi(id: number | string, apiModel: IYieldManagerBidStatApi, previousPeriodStats?: IPreviousPeriodStats) {
    return new YieldManagerBidStat({
      id,
      winRate: apiModel.win_rate,
      bidRate: apiModel.bid_rate,
      responseRate: apiModel.response_rate,
      averageCPM: apiModel.average_cpm,
      averageResponseTime: apiModel.average_response_time,
      efficiency: apiModel.efficiency,
      impressions: apiModel.impressions,
      revenue: apiModel.revenue,
      previousPeriodStats,
    });
  }
}
